import { CalendarDays, Clock, MapPin, Coffee, Languages, Car, ShieldCheck, ReceiptText } from "lucide-react";
import { useSite } from "../../context/SiteContext";
import { EVENT_START } from "../../lib/core";
import { LiteYouTube } from "./LiteYouTube";
import { TicketCtas, SeatLine, BookButton } from "./BookButton";
import { BoxCountdown } from "./Countdown";
import { Container, Label } from "./ui";

const PARTICLES = Array.from({ length: 16 }, (_, i) => ({
  left: `${(i * 37 + 11) % 100}%`,
  size: 2 + (i % 3),
  dur: `${14 + (i % 5) * 3}s`,
  delay: `${-(i * 1.7)}s`,
}));

const Particles = () => (
  <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
    {PARTICLES.map((p, i) => (
      <span key={i} className="particle" style={{ left: p.left, width: p.size, height: p.size, animationDuration: p.dur, animationDelay: p.delay }} />
    ))}
  </div>
);

const DETAILS = [
  [CalendarDays, "Date", "Sunday, 15 November 2026"],
  [Clock, "Time", "9:00 AM – 6:00 PM"],
  [MapPin, "Venue", "Sheraton Grand, Whitefield, Bengaluru"],
  [Coffee, "Includes", "1 Lunch + 2 High Teas"],
  [Languages, "Language", "Hindi & English"],
  [Car, "Parking", "Available at the venue"],
];

const EventDetails = () => (
  <div data-testid="event-details-card" className="ev-card h-full p-5 text-left sm:p-7">
    <Label className="mb-5">Event Details</Label>
    <ul className="grid gap-4">
      {DETAILS.map(([Icon, label, value]) => (
        <li key={label} className="flex items-start gap-3.5">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#F5D77A]">
            <Icon className="h-[18px] w-[18px]" />
          </span>
          <span className="leading-snug">
            <span className="block text-xs font-semibold uppercase tracking-[0.14em] text-[#A39E93]">{label}</span>
            <span data-testid={`event-detail-${label.toLowerCase()}`} className="block text-base font-medium text-[#F5F0E6]">{value}</span>
          </span>
        </li>
      ))}
    </ul>
  </div>
);

const DateBlock = () => (
  <div data-testid="hero-date-block" className="fade-in mx-auto mt-5 max-w-2xl sm:mt-10" style={{ animationDelay: "650ms" }}>
    <div className="gold-rule" />
    <p className="f-cinzel gold-text py-1.5 text-[44px] font-bold leading-none sm:py-3 sm:text-8xl">15 NOV</p>
    <div className="gold-rule" />
    <p className="mt-2.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F5F0E6] sm:mt-4 sm:text-sm sm:tracking-[0.25em]">
      Sunday • 9 AM – 6 PM • Sheraton Grand, Whitefield
    </p>
  </div>
);

const HeroBook = () => {
  const { settings, pricing, fmt } = useSite();
  const t = pricing?.tier || {};
  return (
    <div data-testid="hero-book-block" className="fade-in mx-auto mt-5 max-w-md sm:mt-10" style={{ animationDelay: "750ms" }}>
      <p className="flex flex-wrap items-baseline justify-center gap-x-2.5 gap-y-1">
        <span data-testid="hero-regular-price" className="text-base font-semibold text-[#C9C3B6] line-through decoration-[#D4AF37] decoration-2 sm:text-xl">{fmt(settings?.regular_single_price)} + GST</span>
        <span data-testid="hero-main-price" className="gold-text text-[40px] font-black leading-none tracking-tight sm:text-6xl">{fmt(t.single_price)}</span>
        <span className="text-base font-bold text-[#F5F0E6] sm:text-lg">+ GST</span>
      </p>
      <BookButton plan="single" testid="hero-book-primary" className="mt-4 w-full">Book My Seat – {fmt(t.single_price)} + GST</BookButton>
      <BookButton plan="trio" variant="link" testid="hero-book-trio-link" className="mt-3">Bring 2 friends: 3 seats for {fmt(t.trio_price)} + GST</BookButton>
    </div>
  );
};

const PriceTag = ({ label, regular, current, testid }) => (
  <div data-testid={testid} className="ev-card flex flex-1 flex-col items-center px-4 py-5 sm:px-8">
    <p className="label-xs text-[#D4AF37]">{label}</p>
    <p className="mt-2 text-lg font-semibold text-[#C9C3B6] line-through decoration-[#D4AF37] decoration-2 sm:text-xl">{regular} + GST</p>
    <p className="mt-1 flex items-baseline gap-1.5">
      <span className="gold-text text-[44px] font-black leading-none tracking-tight sm:text-6xl">{current}</span>
      <span className="text-base font-bold text-[#F5F0E6] sm:text-lg">+ GST</span>
    </p>
  </div>
);

const HeroPrices = () => {
  const { settings, pricing, fmt } = useSite();
  const t = pricing?.tier || {};
  return (
    <div className="mx-auto mb-6 grid max-w-3xl gap-3 sm:grid-cols-2 sm:gap-4">
      <PriceTag label="Single Pass" regular={fmt(settings?.regular_single_price)} current={fmt(t.single_price)} testid="hero-price-single" />
      <PriceTag label="Power Trio • 3 Passes" regular={fmt(settings?.regular_trio_price)} current={fmt(t.trio_price)} testid="hero-price-trio" />
    </div>
  );
};

export const Hero = () => {
  const { settings } = useSite();
  const upcoming = Date.now() < EVENT_START;
  return (
    <section id="hero" data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden pb-20 text-center">
      <div aria-hidden className="spotlight pointer-events-none absolute inset-x-0 top-0 h-[900px]" />
      <div aria-hidden className="pointer-events-none absolute left-1/2 top-0 h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-[#D4AF37]/10 blur-[120px]" />
      <Particles />

      <Container className="relative pt-6 md:pt-36">
        <p data-testid="hero-pill" className="label-xs fade-in text-[#D4AF37]">Wealth Accelerator Hub Presents</p>
        <h1 className="f-cinzel mt-3 font-bold leading-[1.02] sm:mt-6">
          <span className="mask-line"><span className="gold-text text-[36px] sm:text-7xl lg:text-[104px]" style={{ animationDelay: "150ms" }}>WEALTH CODE</span></span>
          <span className="mask-line"><span className="gold-text text-[36px] sm:text-7xl lg:text-[104px]" style={{ animationDelay: "300ms" }}>SUMMIT</span></span>
        </h1>
        <p className="fade-in mt-2 text-xs font-semibold tracking-[0.45em] text-[#F5F0E6] sm:mt-5 sm:text-lg sm:tracking-[0.5em]" style={{ animationDelay: "500ms" }}>BENGALURU 2026</p>

        <DateBlock />
        <HeroBook />

        <div className="fade-in mx-auto mt-14 max-w-4xl" style={{ animationDelay: "800ms" }}>
          <Label className="mb-4">Live • In-Person • Bengaluru</Label>
          <h2 data-testid="hero-headline" className="text-[26px] font-bold leading-[1.25] text-[#F5F0E6] sm:text-4xl lg:text-[44px]">
            Decode Your <span className="gold-text">Wealth Code</span>. Remove Your Money Blocks. Build Multiple Sources of Income.
          </h2>
          <p data-testid="hero-subheadline" className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#A39E93] sm:text-lg">
            A full-day, live Summit with ex-banker <strong className="font-semibold text-[#F5F0E6]">Sandeep Gupta</strong>. Discover the money patterns coded into you since childhood, and how to rewrite them for lasting wealth.
          </p>
        </div>

        <div className="fade-in mt-12 grid gap-5 lg:grid-cols-12 lg:gap-7" style={{ animationDelay: "950ms" }}>
          <div className="lg:col-span-7 lg:self-center">
            <LiteYouTube url={settings?.video_url} />
          </div>
          <div className="lg:col-span-5">
            <EventDetails />
          </div>
        </div>

        <div className="mt-10">
          <HeroPrices />
          <TicketCtas />
          <p className="mt-5 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm font-medium text-[#A39E93]">
            <SeatLine testid="seats-remaining-counter" className="font-semibold text-[#F5F0E6]" />
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4 text-[#D4AF37]" /> Secure checkout</span>
            <span className="inline-flex items-center gap-1.5"><ReceiptText className="h-4 w-4 text-[#D4AF37]" /> GST invoice provided</span>
          </p>
        </div>

        {upcoming && (
          <div className="mx-auto mt-12 max-w-2xl">
            <Label className="mb-4">Summit starts in</Label>
            <BoxCountdown target={EVENT_START} testid="hero-countdown" />
          </div>
        )}
      </Container>
    </section>
  );
};

const TICKER = ["DECODE YOUR WEALTH CODE", "REMOVE MONEY BLOCKS", "MULTIPLE INCOME SOURCES", "15 NOV 2026", "SHERATON GRAND WHITEFIELD"];

export const Ticker = () => (
  <div data-testid="editorial-marquee" aria-hidden className="overflow-hidden border-y border-[#D4AF37]/50 bg-[#0A0A0A] py-3.5">
    <div className="ticker-track flex w-max">
      {[0, 1].map((k) => (
        <div key={k} className="flex shrink-0 items-center">
          {TICKER.map((w) => (
            <span key={w} className="flex items-center whitespace-nowrap text-xs font-semibold tracking-[0.3em] text-[#E8D9A8] sm:text-sm">
              <span className="px-5 sm:px-7">{w}</span>
              <span className="gold-text text-base">✦</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);
